import type { BusinessType } from "../types";

interface DataRecoveryModalProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
  data: {
    fullName?: string;
    brandName?: string;
    email?: string;
  } | null;
  isDeleting?: boolean;
}

export default function DataRecoveryModal({
  open,
  onAccept,
  onDecline,
  data,
  isDeleting = false,
}: DataRecoveryModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header with Icon */}
        <div className="px-6 pt-8 pb-4 text-center">
          <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-history-line text-teal-600 text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Previous Data Found
          </h2>
          <p className="text-sm text-gray-500 px-4">
            We found some onboarding progress associated with 
            <span className="font-semibold text-gray-700 ml-1">{data?.email}</span>.
          </p>
        </div>

        {/* Content - Data Preview */}
        {data && (
          <div className="px-6 py-4 mx-6 mb-6 bg-gray-50 rounded-xl border border-gray-100">
            <div className="space-y-3">
              {data.fullName && (
                <div className="flex items-center gap-3">
                  <i className="ri-user-line text-gray-400 text-sm"></i>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Full Name</p>
                    <p className="text-sm text-gray-700 font-medium">{data.fullName}</p>
                  </div>
                </div>
              )}
              {data.brandName && (
                <div className="flex items-center gap-3">
                  <i className="ri-building-line text-gray-400 text-sm"></i>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Brand Name</p>
                    <p className="text-sm text-gray-700 font-medium">{data.brandName}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-6 border-t border-gray-50 bg-gray-50/50 flex flex-col gap-3">
          <p className="text-xs text-gray-500 text-center mb-1">
            Would you like to restore your previous progress?
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onDecline}
              disabled={isDeleting}
              className={`flex-1 px-4 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-all shadow-sm flex justify-center items-center gap-2
                ${isDeleting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-50 hover:border-gray-300 cursor-pointer'}`}
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                </>
              ) : (
                "Start Fresh"
              )}
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-all cursor-pointer shadow-md shadow-teal-500/20"
            >
              Restore Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
